import { differenceInMilliseconds, format, getDate, getMonth, setHours } from "date-fns";
import { Precision } from "@framework/provider-asu";

interface IGetDates {
  startTime: string | Date;
  endTime: string | Date;
  precision: Precision;
}

interface IGetPrecision {
  endTimestamp: string;
  startTimestamp: string;
}

const JOURNAL_CALENDAR_DATE_PATTERN = /^(\d{4})(?:-(\d{2})(?:-(\d{2})(?:T.*)?)?)?$/;

export const parseJournalCalendarDate = (date: string | Date): Date => {
  if (date instanceof Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  const match = JOURNAL_CALENDAR_DATE_PATTERN.exec(date);

  if (!match) {
    return new Date(date);
  }

  const [, year, month = "1", day = "1"] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

export const getJournalHourColumnDate = (dateKey: string, hour: string | number): Date =>
  setHours(parseJournalCalendarDate(dateKey), Number(hour));

export const getJournalHourColumnField = (dateKey: string, hour: string | number): string => {
  const columnDate = getJournalHourColumnDate(dateKey, hour);
  return new Date(columnDate.getTime() - columnDate.getTimezoneOffset() * 60000).toISOString();
};

export const getJournalPointKeysInResponseOrder = (points: ReadonlyArray<{ idPoint: number }>): string[] =>
  Array.from(new Set(points.map(({ idPoint }) => `${idPoint}`)));

export const getDates = ({ startTime, endTime, precision }: IGetDates): any => {
  const dates: any = {};

  // const startDate = new Date(startTime); // для локального времени
  const startDate = new Date(new Date(startTime).getTime() + new Date(startTime).getTimezoneOffset() * 60000);
  // const endDate = new Date(endTime); // для локального времени
  const endDate = new Date(new Date(endTime).getTime() + new Date(endTime).getTimezoneOffset() * 60000);

  if (precision === Precision.month) {
    const startYear = startDate.getUTCFullYear();
    const endYear = endDate.getUTCFullYear();

    for (let i = startYear; i <= endYear; i++) {
      dates[i] = [];
    }

    Object.keys(dates).forEach(key => {
      for (let i = 1; i <= 12; i++) {
        const newDate = new Date(`${key}-${i}-01`);
        const beforeStartDate = newDate.getTime() <= startDate.getTime();
        const isSameMonth = beforeStartDate && getMonth(newDate) === getMonth(startDate);
        const afterStartDate = newDate.getTime() >= startDate.getTime();
        const beforeEndDate = newDate.getTime() <= endDate.getTime();

        if ((isSameMonth || afterStartDate) && beforeEndDate) {
          dates[key].push(`${format(newDate, "yyyy-MM-dd")}T00:00:00.000Z`);
        }
      }
    });
  } else if (precision === Precision.day) {
    const currentDate = new Date(startDate);
    // eslint-disable-next-line no-unmodified-loop-condition
    while (currentDate <= endDate) {
      const month = format(currentDate, "yyyy-MM");
      const day = getDate(currentDate);

      if (!dates[month]) {
        dates[month] = [];
      }

      dates[month].push(day);

      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else if (precision === Precision.hour) {
    const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDateLocal = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), endDate.getHours());
    const startDateStr = format(currentDate, "yyyy-MM-dd");

    // eslint-disable-next-line no-unmodified-loop-condition
    while (currentDate <= endDateLocal) {
      const day = format(currentDate, "yyyy-MM-dd");
      let startHour = 0;
      let endHour = 23;

      if (day === startDateStr) {
        // Если это первый день, начинаем с часа начала
        startHour = startDate.getHours();
      }
      if (day === format(endDateLocal, "yyyy-MM-dd")) {
        // Если это последний день, заканчиваем на часе окончания
        endHour = endDateLocal.getHours();
      }

      // Создаем массив от startHour до endHour
      dates[day] = Array.from({ length: endHour - startHour + 1 }, (_, i) => i + startHour);

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  return dates;
};

export const getPrecision = (props: IGetPrecision): Precision => {
  const { endTimestamp, startTimestamp } = props;

  const endTime = new Date(endTimestamp);
  const startTime = new Date(startTimestamp);

  const diff = differenceInMilliseconds(endTime, startTime);

  const week = 1000 * 60 * 60 * 24 * 7;
  const month = 1000 * 60 * 60 * 24 * 30;

  if (diff <= week) {
    return Precision.hour;
  } else if (diff <= month * 3) {
    return Precision.day;
  } else {
    return Precision.month;
  }
};
